import React from 'react';
import {
  useProgressiveSessionStore,
  VipWebCardListV2,
  VipWebPage,
  VipWebPageReceiveButton,
  VipWebPrizeDetail,
  VipWebLevelReceivedModal,
  VipWebUpgradeModal,
  VipWebWeeklyRewardModal,
  type VipConfig,
  type VipWebCardListV2Ref,
} from '@fe-progressive/package-progressive/vip-web';
import {showProgressiveHostToast} from '@fe-progressive/package-progressive/progressive-toast';
import {goBack, goTo, toPriceStr} from '@/utils';
import globalStore from '@/services/global.state';
import {postUserInfo} from '@/services/global.service';
import {useTranslation} from 'react-i18next';
import walletAnimationJson from '@/components/business/detail-nav-title/wallet-animation-config-web';
import {useWithdrawProgressiveViewportHeightStyle} from '../withdraw/withdraw-progressive-theme';
import {
  checkPopupVipUpgrade,
  getVipConfigListV2,
  receiveVipLevelReward,
  VipConfigVO,
  weekRewardReceive,
} from './vip.service';
import {vipOptionsV2} from './vip.variable';
import './vip-profile-progressive.scss';

function mapVipConfig(item: VipConfigVO): VipConfig {
  return {
    ...item,
    level: item.level ?? 0,
    levelBonus: item.leveBonusAmount,
    needExperience: item.nextLevelExperience,
    status:
      item.rewardVipLevelReceivingStatus === 0 && item.statusReached === 1
        ? 'available'
        : item.rewardVipLevelReceivingStatus === 1
        ? 'received'
        : undefined,
  };
}

const VipNavWalletWeb = () => {
  const walletRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!walletRef.current) {
      return undefined;
    }
    const lottie = require('lottie-web');
    const animation = lottie.loadAnimation({
      container: walletRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: walletAnimationJson,
    });
    const sub = globalStore.amountChanged.subscribe(res => {
      if (
        res.last !== null &&
        res.last < res.current &&
        res.current !== globalStore.userAmount
      ) {
        animation.play();
      }
    });

    return () => {
      animation.destroy();
      sub.unsubscribe();
    };
  }, []);

  return (
    <span className="pg-vip-nav-wallet">
      <span ref={walletRef} className="pg-vip-nav-wallet-lottie" />
    </span>
  );
};

const VipNavAmountSlot = () => {
  const {i18n} = useTranslation();
  const [login, setLogin] = React.useState(Boolean(globalStore.token));
  const [amount, setAmount] = React.useState(globalStore.userAmount);

  React.useEffect(() => {
    const tokenSub = globalStore.tokenSubject.subscribe(token => {
      setLogin(Boolean(token));
    });
    const amountSub = globalStore.amountChanged.subscribe(res => {
      setAmount(res.current);
    });

    return () => {
      tokenSub.unsubscribe();
      amountSub.unsubscribe();
    };
  }, []);

  return (
    <button
      type="button"
      className="pg-vip-nav-amount"
      onClick={() => {
        globalStore.token ? goTo('Recharge') : goTo('Login');
      }}>
      <div className="pg-vip-nav-amount-inner">
        <span className="pg-vip-nav-amount-text">
          <span className="pg-vip-nav-amount-label">
            {i18n.t('label.balance')}
          </span>
          <span className="pg-vip-nav-amount-value">
            {login ? toPriceStr(amount, {suffixUnit: 'K'}) : '-'}
          </span>
        </span>
        <VipNavWalletWeb />
      </div>
    </button>
  );
};

type VipAssetsByLevel = Record<
  number,
  {
    background: string;
    sign: string;
    progressLinear: string;
    titleClassName: string;
    currentTextClassName?: string;
  }
>;

const VipProgressive = () => {
  const {i18n, t} = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [vipList, setVipList] = React.useState<VipConfig[]>([]);
  const [currentVip, setCurrentVip] = React.useState<VipConfig | null>(null);
  const [upgradeExperience, setUpgradeExperience] = React.useState<
    number | null
  >(null);
  const [weeklyReward, setWeeklyReward] = React.useState<{
    baseAmount: number;
    bonusAmount: number;
    multiple: number;
  } | null>(null);
  const vipCardListRef = React.useRef<VipWebCardListV2Ref>(null);
  const progressiveSession = useProgressiveSessionStore;
  const viewportHeightStyle = useWithdrawProgressiveViewportHeightStyle();
  const vipAssetsByLevel = React.useMemo(
    () =>
      vipOptionsV2.reduce<VipAssetsByLevel>(
        (acc, item, index) => ({
          ...acc,
          [index]: {
            background: item.background,
            sign: item.sign,
            progressLinear: item.progressLinear,
            titleClassName: item.titleClass,
            currentTextClassName: item.curTextClass,
          },
        }),
        {},
      ),
    [],
  );

  const syncUser = React.useCallback(async () => {
    const userInfo = globalStore.token ? await postUserInfo() : null;
    if (userInfo) {
      globalStore.userInfo = userInfo;
    }
    progressiveSession.getState().syncSession({
      isLoggedIn: Boolean(globalStore.token),
      tokenReady: true,
      userInfo,
    });
    return userInfo;
  }, [progressiveSession]);

  const fetchVipList = React.useCallback(async () => {
    const list = await getVipConfigListV2();
    const mapped = list.map(mapVipConfig);
    setVipList(mapped);
    setCurrentVip(
      mapped.find(item => item.statusReached === 1) ?? mapped[0] ?? null,
    );
    return mapped;
  }, []);

  const loadVip = React.useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchVipList(), syncUser()]);
    } finally {
      setLoading(false);
    }
  }, [fetchVipList, syncUser]);

  const handleVipChange = React.useCallback((vip: VipConfig) => {
    setCurrentVip(vip);
  }, []);

  React.useEffect(() => {
    loadVip();
    const sub = globalStore.tokenSubject.subscribe(() => {
      syncUser().catch(() => undefined);
    });
    return () => sub.unsubscribe();
  }, [loadVip, syncUser]);

  React.useEffect(() => {
    if (!globalStore.token) {
      return;
    }
    checkPopupVipUpgrade()
      .then(res => {
        if (res?.status) {
          setUpgradeExperience(res.changeExperience || 0);
        }
      })
      .catch(() => undefined);
    weekRewardReceive()
      .then(res => {
        if (res?.weekBaseBonusAmount && res?.bonusAmount) {
          setWeeklyReward({
            baseAmount: res.weekBaseBonusAmount || 0,
            bonusAmount: res.bonusAmount || 0,
            multiple: res.multiple || 0,
          });
        }
      })
      .catch(() => undefined);
  }, []);

  return (
    <div
      className="vip-profile-progressive-page overflow-hidden bg-bg"
      style={viewportHeightStyle}>
      <VipWebPage
        vipList={vipList}
        currentVip={currentVip}
        loading={loading}
        locale={i18n.language}
        currencySymbol={globalStore.currency}
        showAmountSlot
        amountSlot={<VipNavAmountSlot />}
        onBack={goBack}
        onLoginRequired={() => goTo('Login', {backPage: 'Home'})}
        onLevelChange={setCurrentVip}
        renderCardList={() => (
          <VipWebCardListV2
            ref={vipCardListRef}
            isLoggedIn={Boolean(globalStore.token)}
            fetchVipList={fetchVipList}
            assetsByLevel={vipAssetsByLevel}
            currencySymbol={globalStore.currency}
            labels={{
              remainingExperience: t('vip.rechargeTip').replace(
                '{{price}}',
                '{{amount}}',
              ),
              nextLevel: t('vip.incoming'),
              recharge: t('vip.recharge'),
            }}
            onVipChange={handleVipChange}
            onLoginRequired={() => goTo('Login')}
            onRecharge={() => goTo('Recharge')}
          />
        )}
        renderPrizeDetail={() => (
          <VipWebPrizeDetail
            vip={currentVip}
            currencySymbol={globalStore.currency}
            labels={{
              title01: t('vip.tip.levelDetail.t01'),
              title02: t('vip.tip.levelDetail.t02'),
              title03: t('vip.tip.levelDetail.t03'),
              title04: t('vip.tip.levelDetail.t04'),
              title05: t('vip.tip.levelDetail.t05'),
              p1: t('vip.tip.levelDetail.p1'),
              p2: t('vip.tip.levelDetail.p2'),
              p3: t('vip.tip.levelDetail.p3'),
              p4: t('vip.tip.levelDetail.p4'),
              p5: t('vip.tip.levelDetail.p5'),
            }}
            rewardAssets={{
              spin: require('@assets/icons/vip/v2/spin.webp'),
              spinDisabled: require('@assets/icons/vip/v2/spin-gray.webp'),
              scratch: require('@assets/icons/vip/v2/scratch.webp'),
              scratchDisabled: require('@assets/icons/vip/v2/scratch-gray.webp'),
              money: require('@assets/icons/vip/v2/money.webp'),
              moneyDisabled: require('@assets/icons/vip/v2/money-gray.webp'),
            }}
          />
        )}
        renderReceiveAction={() => (
          <VipWebPageReceiveButton
            vip={currentVip}
            label={t('label.receive')}
            currencySymbol={globalStore.currency}
            onReceive={vip => receiveVipLevelReward(Number(vip.level || 0))}
            onReceived={() => {
              showProgressiveHostToast({
                type: 'success',
                message: t('share.copy-success'),
              });
            }}
            onRefresh={() => {
              vipCardListRef.current?.refresh();
              loadVip();
            }}
            renderReceivedModal={({visible, vip, onClose}) => (
              <VipWebLevelReceivedModal
                visible={visible}
                amount={vip?.levelBonus}
                locale={i18n.language}
                currencySymbol={globalStore.currency}
                onClose={onClose}
              />
            )}
          />
        )}
      />
      <VipWebUpgradeModal
        visible={upgradeExperience != null}
        amount={upgradeExperience ?? undefined}
        locale={i18n.language}
        currencySymbol={globalStore.currency}
        onClose={() => setUpgradeExperience(null)}
      />
      <VipWebWeeklyRewardModal
        visible={weeklyReward != null}
        amount={weeklyReward?.bonusAmount}
        baseAmount={weeklyReward?.baseAmount}
        multiple={weeklyReward?.multiple}
        locale={i18n.language}
        currencySymbol={globalStore.currency}
        onClose={() => setWeeklyReward(null)}
      />
    </div>
  );
};

export default VipProgressive;
